<section class="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-950 shadow-xs relative group transition-all">
  <div class="relative w-full aspect-[21/9] sm:aspect-[24/9] min-h-[160px] sm:min-h-[200px] overflow-hidden bg-slate-900">
    <img
      [src]="effectiveStadiumImage()"
      [alt]="stadiumDisplayName()"
      referrerpolicy="no-referrer"
      class="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out"
    />
    <!-- Gradient Overlay for High Contrast & Readability -->
    <div class="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/25 to-slate-950/40"></div>

    <!-- Top Left: Venue Type / Stadium Badge -->
    <div class="absolute top-3 left-3 sm:top-4 sm:left-4 flex items-center space-x-2">
      <span
        class="inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-lg text-[10px] sm:text-xs font-bold uppercase tracking-wider backdrop-blur-md shadow-sm border bg-emerald-950/80 text-emerald-300 border-emerald-500/30"
      >
        <app-icon name="building" [size]="13" class="text-emerald-400" />
        <span>Estadio Oficial</span>
      </span>
      @if (loading() && !editing()) {
        <span class="inline-flex items-center space-x-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-slate-800/80 text-slate-300 backdrop-blur-xs animate-pulse">
          Cargando…
        </span>
      }
    </div>

    <!-- Top Right: Rename Button -->
    <div class="absolute top-3 right-3 sm:top-4 sm:right-4">
      <button
        type="button"
        (click)="openEditor()"
        [disabled]="loading() || saving()"
        class="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white/95 bg-black/60 hover:bg-black/85 backdrop-blur-md border border-white/20 transition-all cursor-pointer shadow-sm active:scale-95 disabled:opacity-50"
        title="Cambiar nombre del estadio"
      >
        <app-icon name="edit" [size]="13" />
        <span>Cambiar nombre</span>
      </button>
    </div>

    <!-- Bottom Content: Stadium Name + Capacity + Star Rating (1-5 with half-stars) -->
    <div class="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
      <div class="min-w-0 flex-1">
        <h3 class="text-lg sm:text-xl md:text-2xl font-extrabold text-white tracking-tight drop-shadow-md truncate">
          {{ stadiumDisplayName() }}
        </h3>
        <div class="flex items-center flex-wrap gap-2 text-xs text-slate-300 mt-1">
          <span class="flex items-center space-x-1 text-slate-200">
            <app-icon name="users" [size]="13" class="text-emerald-400 shrink-0" />
            <span class="font-bold">{{ effectiveCapacity().toLocaleString() }}</span>
            <span class="text-slate-400 font-normal">aforo</span>
          </span>
          <span class="text-slate-500">•</span>
          <span class="text-slate-300 text-[11px] sm:text-xs font-medium">{{ effectiveCategory() }}</span>
        </div>
      </div>

      <!-- 1-5 Stars Rating Level with Half-Stars -->
      <div class="flex items-center space-x-2 bg-black/75 backdrop-blur-md px-3 py-1.5 rounded-xl border border-white/15 shrink-0 self-start sm:self-end shadow-md">
        <div class="flex items-center space-x-1" [title]="'Estadio Nivel ' + effectiveRating().toFixed(1) + ' de 5 estrellas'">
          @for (star of stadiumStarsArray; track star) {
            @if (effectiveRating() >= star) {
              <app-icon
                name="star"
                [size]="15"
                class="text-amber-400 fill-amber-400 drop-shadow-sm"
              />
            } @else if (effectiveRating() >= star - 0.5) {
              <app-icon
                name="star-half"
                [size]="15"
                class="text-amber-400 drop-shadow-sm"
              />
            } @else {
              <app-icon
                name="star"
                [size]="15"
                class="text-slate-600 fill-none"
              />
            }
          }
        </div>
        <span class="text-xs font-bold font-mono text-amber-300 ml-1">
          {{ effectiveRating().toFixed(1) }}
        </span>
      </div>
    </div>
  </div>

  @if (error() && !editing()) {
    <div class="p-3 bg-rose-50 dark:bg-rose-950/60 border-t border-rose-200 dark:border-rose-800 flex items-center justify-between text-xs text-rose-700 dark:text-rose-300">
      <p role="alert">{{ error() }}</p>
      <button type="button" (click)="load()" [disabled]="loading() || saving()" class="underline font-bold ml-2">Reintentar</button>
    </div>
  }
  @if (success()) {
    <p role="status" class="sr-only">Nombre guardado.</p>
  }
</section>

<dialog #nameDialog (cancel)="onDialogCancel($event)" (close)="onDialogClosed()"
  aria-labelledby="stadium-name-dialog-title"
  class="m-auto w-[calc(100%-2rem)] max-w-lg max-h-[calc(100%-2rem)] overflow-y-auto rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-0 text-slate-900 dark:text-white shadow-2xl backdrop:bg-slate-900/60 backdrop:backdrop-blur-xs">
  <form (ngSubmit)="save()" class="p-6 space-y-5">
    <div class="flex items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-3">
      <div class="flex items-center gap-2"><app-icon name="building" [size]="20" class="text-emerald-600 dark:text-emerald-400" /><h3 id="stadium-name-dialog-title" class="text-base font-bold">Cambiar nombre del estadio</h3></div>
      <button type="button" (click)="closeEditor()" [disabled]="saving() || loading()" aria-label="Cerrar"
        class="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-50"><app-icon name="x" [size]="18" /></button>
    </div>
    @if (current(); as stadium) {
      <div class="space-y-2">
        <label for="stadium-name" class="block text-xs font-semibold text-slate-700 dark:text-slate-300">Nombre del estadio</label>
        <input id="stadium-name" name="stadiumName" [(ngModel)]="draft" required maxlength="100" autofocus
          [disabled]="saving() || loading()" aria-describedby="stadium-name-cost"
          class="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-emerald-500" />
        @if (stadium.sponsorName) {
          <p class="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">Escribe solo el nombre. El prefijo {{ stadium.sponsorName }} se añade automáticamente.</p>
        }
      </div>
      <p id="stadium-name-cost" class="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-400">
        @if (stadium.nextRenameCost === 0) {
          El primer nombre es gratuito.
        } @else {
          El cambio cuesta {{ stadium.nextRenameCost | currency:'EUR':'symbol':'1.0-2' }} y se cobra al guardar.
        }
      </p>
    }
    @if (loading()) {
      <p role="status" class="text-sm text-slate-500">Actualizando nombre…</p>
    }
    @if (error()) {
      <div class="space-y-2">
        <p role="alert" class="p-3 bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-800 rounded-xl text-rose-700 dark:text-rose-300 text-xs">{{ error() }}</p>
        <button type="button" (click)="load()" [disabled]="loading() || saving()"
          class="text-sm underline text-slate-600 dark:text-slate-300">Actualizar nombre</button>
      </div>
    }
    <div class="flex items-center justify-end gap-2.5 pt-2 border-t border-slate-100 dark:border-slate-800">
      <button type="button" (click)="closeEditor()" [disabled]="saving() || loading()"
        class="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold transition-colors cursor-pointer disabled:opacity-50">Cancelar</button>
      <button type="submit" [disabled]="!canSave"
        class="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold shadow-xs transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5">
        <app-icon name="check-circle" [size]="14" [class.animate-spin]="saving()" />
        <span>{{ saving() ? 'Guardando…' : 'Guardar nombre' }}</span>
      </button>
    </div>
  </form>
</dialog>
